import dayjs from "dayjs";
import { useCookie } from "@/composables/useCookie";
import { generateId } from "@/utils/generateId";
import { LocalStorageKeys } from "@/constants/storageKeys";
import { AUTH_TOKEN_COOKIE, LOGIN_PATH, SIGNED_OUT_PATH, START_PATH } from "@/constants/auth";
import type { AuthSession } from "@/types/auth";

let currentSessionId: string | null = null;
let initialized = false;

function readAuthSession(): AuthSession | null {
  const raw = localStorage.getItem(LocalStorageKeys.authSession);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    localStorage.removeItem(LocalStorageKeys.authSession);
    return null;
  }
}

function writeAuthSession(dispatcherId: number | null, companyId: number | null): string {
  const session: AuthSession = {
    sessionId: generateId(),
    dispatcherId,
    companyId,
    at: dayjs().valueOf(),
  };

  localStorage.setItem(LocalStorageKeys.authSession, JSON.stringify(session));

  return session.sessionId;
}

function hasAuthToken(): boolean {
  return useCookie(AUTH_TOKEN_COOKIE).get() !== null;
}

function isOnLoginPage(): boolean {
  return window.location.pathname === LOGIN_PATH;
}

export function useAuthSession() {
  const publishLogin = (dispatcherId: number | null, companyId: number | null): void => {
    currentSessionId = writeAuthSession(dispatcherId, companyId);
  };

  const publishLogout = (): void => {
    currentSessionId = null;
    localStorage.removeItem(LocalStorageKeys.authSession);
  };

  const adoptSession = (): void => {
    const session = readAuthSession();

    if (session) {
      currentSessionId = session.sessionId;
      return;
    }

    if (hasAuthToken()) {
      currentSessionId = writeAuthSession(null, null);
    }
  };

  const reconcileSession = (): void => {
    const session = readAuthSession();

    if (!session) {
      if (currentSessionId && !isOnLoginPage()) {
        currentSessionId = null;
        window.location.assign(SIGNED_OUT_PATH);
      }
      return;
    }

    if (session.sessionId === currentSessionId) {
      return;
    }

    if (isOnLoginPage()) {
      window.location.assign(START_PATH);
      return;
    }

    window.location.reload();
  };

  const initAuthSessionSync = (): void => {
    if (initialized) {
      return;
    }

    initialized = true;
    adoptSession();

    window.addEventListener("storage", (event) => {
      if (event.key && event.key !== LocalStorageKeys.authSession) {
        return;
      }

      reconcileSession();
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        reconcileSession();
      }
    });
  };

  return { initAuthSessionSync, publishLogin, publishLogout };
}
